<template>
  <div v-if="isLoaded" class="portal mh-100vh d-flex flex-column mt-0" :class="{ 'no-header': isAppHeaderHidden }"
    :id="containerId" :style="styleCustomizations"
    :data-borrower-type="applicationsLoaded ? (returnAppExperience ? 'returning' : 'first-time') : null">
    <BankPullFailureToastHost />
    <MainBanner lg isPortal />
    <MainBanner sm isPortal />
    <LayoutBasePortal></LayoutBasePortal>
    <div data-smooth-scroll="main" class="portal-content d-flex flex-grow-1" :class="[
      appStore.mainBanner.show ? 'banner-is-showing' : null
    ]">
      <PortalContainer />
    </div>
    <AgentPanelHost :agent-key="MERCHANT_INTERVIEW_AGENT_KEY" :invitation-for="merchantInterviewInvitation"
      @impression="trackAgentPanel('Agent Panel Shown', 'MI Invitation Shown')"
      @collapsed="trackAgentPanel('Agent Panel Collapsed')" @minimized="trackAgentPanel('Agent Panel Minimized')"
      @engaged="trackAgentPanel('Agent Panel Engaged')"
      @completed="trackAgentPanel('MI Completed')" @abandoned="trackAgentPanel('MI Abandoned')"
      @expanded-change="onAgentPanelExpandedChange" />
  </div>
  <div v-else></div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/store/app'

// Components
import PortalContainer from '~/components/portal/PortalContainer'
import LayoutBasePortal from './base-portal'
import BankPullFailureToastHost from '~/components/bank-pull-status/BankPullFailureToastHost.vue'
import AgentPanelHost from '~/components/portal/ai/AgentPanelHost.vue'

// Helpers
import { getStyleProps, DEFAULT_THEME_COLOR } from '~/libs/customizations'
import {
  MERCHANT_INTERVIEW_AGENT_KEY,
  merchantInterviewInvitation,
} from '~/libs/merchant-interview'

// Stores
import { useAppAnalyticsStore } from '~/store/app-analytics'
import { useApplicationConfigStore } from '~/store/application-config'
import { usePortalStore } from '~/store/portal'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { useProgressStore } from '~/store/progress'
import { get } from 'es-toolkit/compat'
import { useMissingStatementsBanner } from '~/composables/banner/useMissingStatementsBanner'
import { useStatementPullStatus } from '~/composables/useStatementPullStatus'
import { useBankConnectorStore } from '~/store/bank-connector'

const appStore = useAppStore()
const appAnalyticsStore = useAppAnalyticsStore()
const appConfigStore = useApplicationConfigStore()

const portalStore = usePortalStore()
const affiliateCustomizationStore = useAffiliateCustomizationStore()
const progressStore = useProgressStore()
const bankConnectorStore = useBankConnectorStore()

const { isQualifiedBorrower } = storeToRefs(portalStore)
const { returnAppExperience, applicationsLoaded } = storeToRefs(progressStore)

useMissingStatementsBanner()
useStatementPullStatus()
appConfigStore.updateBankPullStatusIndicatorEnabled()

// Hydrates status on load/refresh, since Pinia state resets and the Pusher
// subscription above only catches pushes received while mounted.
onMounted(() => {
  bankConnectorStore.getStatementPullStatus()
})

const packageReadyUnlocked = ref(false)
portalStore.detectPackageReadyAction().then(() => {
  packageReadyUnlocked.value = true
})

watch([packageReadyUnlocked, () => portalStore.packageReadyNotificationAlert], ([unlocked, msg]) => {
  if (appStore.mainBanner.label === 'finicityBanner') return

  if (unlocked && msg) {
    appStore.addBanner({ type: 'warning', title: 'Needs Attention', msg, label: 'packageReadyNotification' })
  } else if (!msg) {
    appStore.clearBanner('packageReadyNotification')
  }
})

watch(applicationsLoaded, (loaded) => {
  if (loaded && typeof window !== 'undefined') {
    appAnalyticsStore.fsTrackEvent({
      name: 'Portal Viewed',
      properties: { borrowerType: returnAppExperience.value ? 'returning' : 'first-time' },
    })
  }
}, { immediate: true })

// Shown / Collapsed / Minimized / Engaged stay separate events — one number can't tell a
// borrower who ignored the assistant from one who never noticed it.
// MI-named events are the metric funnel (permission/completion denominators).
// the expanded panel and Intercom's launcher share the corner — one at a time
function onAgentPanelExpandedChange(expanded) {
  useNuxtApp().$chatService?.setLauncherVisibility(!expanded)
}

function trackAgentPanel(name, miName = null) {
  appAnalyticsStore.fsTrackEvent({
    name,
    properties: { agent: MERCHANT_INTERVIEW_AGENT_KEY },
  })

  if (miName) {
    appAnalyticsStore.fsTrackEvent({
      name: miName,
      properties: { agent: MERCHANT_INTERVIEW_AGENT_KEY },
    })
  }
}

const isLoaded = computed(() => {
  return !affiliateCustomizationStore.isEmbeddedLayout
    || Boolean(affiliateCustomizationStore.affiliateCustomizations)
})

const containerId = computed(() => {
  if (affiliateCustomizationStore.isEmbeddedLayout) {
    return 'portal'
  }
  return isQualifiedBorrower.value ? 'borrower-qualified' : 'borrower-dnq'
})

// 4. Window / Responsive Logic
const windowSize = ref(typeof window !== 'undefined' ? window.innerWidth : 0)

function windowResize() {
  windowSize.value = window.innerWidth
}

const isMobile = computed(() => windowSize.value < 768)

onMounted(() => {
  window.addEventListener('resize', windowResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', windowResize)
  // Intercom's launcher setting is window-level — restore it when the panel's layout goes away
  useNuxtApp().$chatService?.setLauncherVisibility(true)
})

// 5. Dynamic Styling & SEO/Head
const styleCustomizations = computed(() => {
  if (affiliateCustomizationStore.isEmbeddedLayout) {
    return affiliateCustomizationStore.appStyleProps
  }
  return getStyleProps()
})

const themeColor = computed(() => {
  // TODO: embedded cleanup
  if (affiliateCustomizationStore.isAppHeaderHidden) {
    return DEFAULT_THEME_COLOR
  }
  return get(affiliateCustomizationStore, 'appStyleProps.--app-aside-background-color', DEFAULT_THEME_COLOR)
})

const isAppHeaderHidden = computed(() => affiliateCustomizationStore.isAppHeaderHidden)

useHead({
  meta: [
    {
      name: 'theme-color',
      content: themeColor
    },
  ],
})
</script>

<style lang="scss">
#portal {
  @include affiliateCustomizedApp();
}

.mh-100vh {
  min-height: 100vh;
}

.portal-content {
  @include media-breakpoint-down(lg) {
    margin-top: 76px;
  }
}

.banner-is-showing {
  @include media-breakpoint-up(lg) {
    --portal-banner-height: 85px;
    margin-top: var(--portal-banner-height);
  }

  @include media-breakpoint-down(lg) {
    margin-top: 0;
  }
}

.portal {
  background-color: $neutral-100;

  .portal-content>.sidebar-section {
    display: none;
  }

  .portal-content:has(> .sidebar-section) {
    &>.sidebar-section {
      @include media-breakpoint-up(md) {
        display: block;
      }
    }

    @include media-breakpoint-up(lg) {
      .content {
        width: auto;
        flex: 1 1 0 !important;
      }

      #side-nav {
        flex: 0.55553 1 0;
      }
    }
  }

  .portal-content:has(> .sidebar-section) {
    .content-wrap {
      @include media-breakpoint-down(xxl) {
        margin-bottom: 192px;
      }

      @include media-breakpoint-down(lg) {
        margin-bottom: 282px;
      }

      @include media-breakpoint-down(md) {
        margin-bottom: 325px;
      }
    }
  }
}
</style>
